import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { storageService } from '@/services/local-storage';
import { ScoringService } from '@/services/scoring';
import type { EvaluationSummary } from '@/services/scoring';

/**
 * Hook to calculate and fetch evaluation results with MCDA scoring
 *
 * @param evaluationId - Evaluation ID to calculate results for
 * @returns Query result with evaluation summary or error
 *
 * @example
 * ```tsx
 * function EvaluationResultsPage() {
 *   const { id } = useParams();
 *   const { data: summary, isLoading, error, refetch } = useEvaluationResults(id);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!summary) return <NotFound />;
 *
 *   return (
 *     <div>
 *       <button onClick={() => refetch()}>Refresh Results</button>
 *       <SolutionRankings summary={summary} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useEvaluationResults(
  evaluationId: string | undefined
): UseQueryResult<EvaluationSummary, Error> {
  return useQuery({
    queryKey: queryKeys.evaluations.results(evaluationId || ''),
    queryFn: () => {
      if (!evaluationId) {
        throw new Error('Evaluation ID is required');
      }

      const evaluation = storageService.getEvaluation(evaluationId);
      if (!evaluation) {
        throw new Error(`Evaluation ${evaluationId} not found`);
      }

      const scorecards = storageService.getAllScorecardsByEvaluation(evaluationId);
      return ScoringService.calculateEvaluationSummary(evaluation, scorecards);
    },
    enabled: !!evaluationId, // Only run query if evaluationId is provided
    // Refetch on window focus to ensure data is up-to-date when user switches back
    refetchOnWindowFocus: true,
    // Stale time of 0 means data is always considered stale and will refetch
    // This ensures MCDA calculations are always fresh when data changes
    staleTime: 0,
  });
}
